import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { Button, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TableContainer } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchBar from "../../components/SearchBar";
import SwitchToggle from "../../components/SwitchToggle";
import AddFlavor from "../../Modals/addflavor";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import { useGetFlavourListQuery, useDeleteFlavourMutation, useUpdateFlavourStatusMutation } from "../../store/api/commonAPI";
import Loader from "../../constants/Loader";
import { showToast, showError } from "../../constants/toasts";

const ManageFlavor = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  // Helper function to determine if a color is light (needs darker border)
  const isLightColor = (color: string): boolean => {
    if (!color) return false;
    // Remove # if present
    const hex = color.replace("#", "");
    if (hex.length !== 6) return false;
    
    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Calculate brightness (using relative luminance formula)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    // Consider colors with brightness > 200 as light (white is 255)
    return brightness > 200;
  };

  // Fetch flavours from API
  const { data, isLoading, error, refetch } = useGetFlavourListQuery({
    page,
    limit,
  });

  // Delete flavour mutation
  const [deleteFlavour, { isLoading: isDeleting }] = useDeleteFlavourMutation();

  // Update flavour status mutation
  const [updateFlavourStatus, { isLoading: isUpdatingStatus }] = useUpdateFlavourStatusMutation();

  // Extract flavours from API response
  const flavors = useMemo(() => {
    if (data?.data?.flavour && Array.isArray(data.data.flavour)) {
      return data.data.flavour.map((flavor) => ({
        id: flavor._id || flavor.id,
        name: flavor.name,
        color: flavor.colourCode || flavor.color || "#000000",
        status: flavor.isActive ?? flavor.status ?? true,
      }));
    }
    return [];
  }, [data]);

  // Filter flavours based on search term
  const filteredFlavors = useMemo(() => {
    if (!searchTerm) return flavors;
    return flavors.filter((f) =>
      f.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [flavors, searchTerm]);

  const [openModal, setOpenModal] = useState(false);
  const [editFlavor, setEditFlavor] = useState<{ id: string; name: string; color: string; status: boolean } | null>(null);
  
  const handleCloseModal = () => {
    setOpenModal(false);
    setEditFlavor(null);
    // Refetch data after modal closes (in case a new flavour was added or updated)
    refetch();
  };

  // Handle edit button click
  const handleEditClick = (flavor: { id: string; name: string; color: string; status: boolean }) => {
    setEditFlavor(flavor);
    setOpenModal(true);
  };

  // Delete confirmation modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [flavorToDelete, setFlavorToDelete] = useState<{ id: string; name: string } | null>(null);

  // Handle delete button click - opens confirmation modal
  const handleDeleteClick = (id: string, name: string) => {
    setFlavorToDelete({ id, name });
    setOpenDeleteModal(true);
  };

  // Handle delete confirmation - actually deletes the flavour
  const handleConfirmDelete = async () => {
    if (!flavorToDelete) return;

    try {
      const response = await deleteFlavour({ id: flavorToDelete.id }).unwrap();
      
      // Show success message from API response or default message
      const successMessage = response?.message || `Flavour "${flavorToDelete.name}" deleted successfully`;
      showToast(successMessage);
      
      // Refetch the list after successful deletion
      refetch();
      // Close modal and reset state
      setOpenDeleteModal(false);
      setFlavorToDelete(null);
    } catch (error: any) {
      console.error("Error deleting flavour:", error);
      
      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to delete flavour. Please try again.";
      
      showError(errorMessage);
    }
  };

  // Handle delete modal close
  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setOpenDeleteModal(false);
      setFlavorToDelete(null);
    }
  };

  // Handle toggle status change
  const handleStatusToggle = async (flavorId: string, flavorName: string, currentStatus: boolean) => {
    const newStatus = !currentStatus;
    
    try {
      const response = await updateFlavourStatus({ 
        id: flavorId, 
        isActive: newStatus 
      }).unwrap();
      
      // Show success message from API response or default message
      const successMessage = response?.message || 
        `Flavour "${flavorName}" ${newStatus ? "activated" : "deactivated"} successfully`;
      showToast(successMessage);
      
      // Refetch the list to get updated data
      refetch();
    } catch (error: any) {
      console.error("Error updating flavour status:", error);
      
      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.message ||
        "Failed to update flavour status. Please try again.";
      
      showError(errorMessage);
      
      // Refetch to revert the UI state if update failed
      refetch();
    }
  };
  return (
    <>
      <Loader isLoad={isLoading} />
      <div className="main_title">
        <h1>Manage Flavor</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> - Manage Flavor
        </p>
      </div>

      <div className="cards">
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Flavors..."
            />
          </div>
          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => {
                setEditFlavor(null);
                setOpenModal(true);
              }}
            >
              <AddIcon /> Add Flavor
            </Button>
          </div>
        </div>

        {/* Flavor Table */}
        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sr. No</TableCell>
                <TableCell>Flavor Name</TableCell>
                <TableCell>Flavor Color Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {error ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    Error loading flavours. Please try again.
                  </TableCell>
                </TableRow>
              ) : filteredFlavors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    {isLoading ? "Loading..." : "No flavours found"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredFlavors.map((flavor, index) => (
                  <TableRow key={flavor.id}>
                    <TableCell>{(page - 1) * limit + index + 1}</TableCell>
                    <TableCell>{flavor.name}</TableCell>
                    <TableCell>
                      <span className="color_code">
                        <span 
                          className="color_box" 
                          style={{ 
                            backgroundColor: flavor.color,
                            border: `1px solid ${isLightColor(flavor.color) ? "#cccccc" : "#e0e0e0"}`
                          }}
                        ></span> {flavor.color}
                      </span>
                    </TableCell>
                    <TableCell>
                      <SwitchToggle 
                        checked={flavor.status} 
                        onChange={() => flavor.id && handleStatusToggle(flavor.id, flavor.name, flavor.status)}
                        disabled={isUpdatingStatus || !flavor.id}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton 
                          onClick={() => {
                            if (flavor.id) {
                              handleEditClick({
                                id: flavor.id,
                                name: flavor.name,
                                color: flavor.color,
                                status: flavor.status,
                              });
                            }
                          }}
                          disabled={!flavor.id}
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton
                          onClick={() => flavor.id && handleDeleteClick(flavor.id, flavor.name)}
                          disabled={isDeleting || !flavor.id}
                        >
                          <img src="/static/images/trash2_icon.svg" alt="Delete" />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
      <AddFlavor
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        editFlavor={editFlavor ? {
          _id: editFlavor.id,
          name: editFlavor.name,
          colourCode: editFlavor.color,
          isActive: editFlavor.status,
        } : null}
      />
      <DeleteConfirmationModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        setOpen={setOpenDeleteModal}
        onConfirm={handleConfirmDelete}
        itemName={flavorToDelete?.name}
        isLoading={isDeleting}
      />
    </>
  );
};

export default ManageFlavor;
